import { serializeMaipoPublicScene } from './canonical';
import type { MaipoPublicSceneV00, MaipoPublicSceneV01, PublicFormatDiagnostic } from './types';
import { parseMaipoPublicScene, parseMaipoPublicSceneV00 } from './validation';

export const MAIPO_PUBLIC_SCENE_CURRENT_VERSION = '0.1' as const;
export const MAIPO_PUBLIC_SCENE_AUTHORING_VERSIONS = ['0.1'] as const;
export const MAIPO_PUBLIC_SCENE_MIGRATION_SOURCE_VERSIONS = ['0.0', '0.1'] as const;
/** @deprecated Use the explicit authoring or migration-source version list. */
export const MAIPO_PUBLIC_SCENE_VERSIONS = MAIPO_PUBLIC_SCENE_MIGRATION_SOURCE_VERSIONS;
export type MaipoPublicSceneVersion = typeof MAIPO_PUBLIC_SCENE_MIGRATION_SOURCE_VERSIONS[number];

const V00_TO_V01_STEPS = [
  'scene-0.0-rename-envelope-fields',
  'scene-0.0-normalize-srgb-canvas',
] as const;

export interface MaipoPublicSceneMigrationResult {
  fromVersion: MaipoPublicSceneVersion;
  toVersion: typeof MAIPO_PUBLIC_SCENE_CURRENT_VERSION;
  steps: string[];
  diagnostics: PublicFormatDiagnostic[];
  scene: MaipoPublicSceneV01;
  canonical: string;
}

export class UnsupportedMaipoSceneVersionError extends Error {
  readonly code = 'unsupported-scene-version';
  constructor(readonly receivedVersion: string, readonly targetVersion: string) {
    super(`No Maipo scene migration route exists from ${receivedVersion} to ${targetVersion}.`);
    this.name = 'UnsupportedMaipoSceneVersionError';
  }
}

export function readMaipoPublicSceneVersion(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const format = record.format;
  const version = record.version;
  return format === 'maipo-scene' && typeof version === 'string' ? version : null;
}

function migrateSceneV00ToV01(source: MaipoPublicSceneV00): MaipoPublicSceneV01 {
  return {
    $schema: 'https://maipo.design/schema/0.1/scene.schema.json',
    format: 'maipo-scene',
    version: '0.1',
    documentId: source.id,
    revision: source.revision,
    name: source.title,
    canvas: {
      width: source.canvas.width,
      height: source.canvas.height,
      colorSpace: 'srgb',
      background: source.canvas.backgroundColor === null
        ? { type: 'transparent' }
        : { type: 'solid', color: source.canvas.backgroundColor },
    },
    fonts: structuredClone(source.fonts),
    assets: structuredClone(source.assets),
    children: structuredClone(source.nodes),
    ...(source.extensionDeclarations === undefined ? {} : { extensionDeclarations: structuredClone(source.extensionDeclarations) }),
    ...(source.extensions === undefined ? {} : { extensions: structuredClone(source.extensions) }),
  };
}

/**
 * Explicit migration boundary for public scenes. Every route validates its
 * exact source contract and returns canonical, target-validated 0.1 bytes.
 */
export function migrateMaipoPublicScene(
  value: unknown,
  options: { toVersion?: typeof MAIPO_PUBLIC_SCENE_CURRENT_VERSION } = {},
): MaipoPublicSceneMigrationResult {
  const target = options.toVersion ?? MAIPO_PUBLIC_SCENE_CURRENT_VERSION;
  const received = readMaipoPublicSceneVersion(value);
  if ((received !== '0.0' && received !== '0.1') || target !== MAIPO_PUBLIC_SCENE_CURRENT_VERSION) {
    throw new UnsupportedMaipoSceneVersionError(received ?? 'unknown', target);
  }
  const validated = received === '0.0'
    ? migrateSceneV00ToV01(parseMaipoPublicSceneV00(structuredClone(value)))
    : parseMaipoPublicScene(structuredClone(value));
  const canonical = serializeMaipoPublicScene(validated);
  const scene = parseMaipoPublicScene(JSON.parse(canonical));
  return {
    fromVersion: received,
    toVersion: '0.1',
    steps: received === '0.0' ? [...V00_TO_V01_STEPS] : [],
    diagnostics: [],
    scene,
    canonical,
  };
}
